// API-методы для управления passkey'ями (enrollment + список + удаление)
// и regenerate recovery codes. См. ANX-013 backend handlers в
// internal/http/passkeys_handler.go.
//
// Тип `WebAuthnOptions` намеренно широкий — это сырые
// PublicKeyCredentialCreationOptions / PublicKeyCredentialRequestOptions
// от go-webauthn, которые `@simplewebauthn/browser` принимает как есть.
import type { ApiClient } from './client';

export interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
}

interface RawPasskey {
  id: string;
  name: string;
  created_at: string;
  last_used_at?: string;
}

function toPasskey(raw: RawPasskey): Passkey {
  const p: Passkey = {
    id: raw.id,
    name: raw.name,
    createdAt: raw.created_at,
  };
  if (raw.last_used_at) p.lastUsedAt = raw.last_used_at;
  return p;
}

export interface BeginRegisterResponse {
  /** Сырые options для navigator.credentials.create / SimpleWebAuthn startRegistration. */
  options: unknown;
  sessionToken: string;
}

export interface FinishRegisterResponse {
  passkey: Passkey;
  /** Recovery codes — массив plain-строк, выдаётся ТОЛЬКО при первом
   * passkey (или если пусто). Показывать пользователю один раз. */
  recoveryCodes?: string[];
}

export interface RegenerateRecoveryResponse {
  recoveryCodes: string[];
}

// ===== Enrollment (operator JWT) ===========================================

export async function listPasskeys(client: ApiClient): Promise<Passkey[]> {
  const raw = await client.request<{ passkeys: RawPasskey[] }>('/api/v1/me/passkeys');
  return raw.passkeys.map(toPasskey);
}

export async function beginRegisterPasskey(client: ApiClient): Promise<BeginRegisterResponse> {
  const raw = await client.request<{ options: unknown; session_token: string }>(
    '/api/v1/me/passkeys/begin-register',
    { method: 'POST', json: {} },
  );
  return { options: raw.options, sessionToken: raw.session_token };
}

export async function finishRegisterPasskey(
  client: ApiClient,
  input: { sessionToken: string; name: string; attestation: unknown },
): Promise<FinishRegisterResponse> {
  const raw = await client.request<{ passkey: RawPasskey; recovery_codes?: string[] }>(
    '/api/v1/me/passkeys/finish-register',
    {
      method: 'POST',
      json: {
        session_token: input.sessionToken,
        name: input.name,
        attestation: input.attestation,
      },
    },
  );
  const out: FinishRegisterResponse = { passkey: toPasskey(raw.passkey) };
  if (raw.recovery_codes && raw.recovery_codes.length > 0) {
    out.recoveryCodes = raw.recovery_codes;
  }
  return out;
}

export async function deletePasskey(client: ApiClient, id: string): Promise<void> {
  await client.request<undefined>(`/api/v1/me/passkeys/${id}`, { method: 'DELETE' });
}

export async function regenerateRecoveryCodes(
  client: ApiClient,
): Promise<RegenerateRecoveryResponse> {
  const raw = await client.request<{ recovery_codes: string[] }>(
    '/api/v1/me/recovery-codes/regenerate',
    { method: 'POST', json: {} },
  );
  return { recoveryCodes: raw.recovery_codes };
}
